import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Briefcase } from 'lucide-react';
import {
  EditModeProvider,
  EditModeContext,
  AdminToolbar,
  EditableText
} from '../components/EditableContent';
import CMSBlockRenderer from '../components/CMSBlockRenderer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ProjectsPageContent() {
  const { pageContent, setPageContent } = useContext(EditModeContext);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/projects`);
      setProjects(res.data);
    } catch (err) {
      console.error('Error fetching projects:', err);
    } finally {
      setLoading(false);
    }
  };

  const getBlockContent = (type, defaultContent = {}) => {
    const block = pageContent?.blocks?.find(b => b.type === type);
    return block?.content || defaultContent;
  };

  const updateBlockContent = (type, updates) => {
    if (!pageContent) return;
    const blockIndex = pageContent.blocks?.findIndex(b => b.type === type);
    if (blockIndex >= 0) {
      const newBlocks = [...pageContent.blocks];
      newBlocks[blockIndex] = { ...newBlocks[blockIndex], content: { ...newBlocks[blockIndex].content, ...updates } };
      setPageContent({ ...pageContent, blocks: newBlocks });
    }
  };

  const heroContent = getBlockContent('hero', {
    title: 'Customer Projects',
    subtitle: 'See how our products are being used in real-world installations across Australia.',
  });

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="projects-page">
        <div className="text-center mb-12">
          <EditableText
            value={heroContent.title || 'Customer Projects'}
            onChange={(val) => updateBlockContent('hero', { title: val })}
            tag="h1"
            className="text-3xl sm:text-4xl font-black text-foreground uppercase tracking-tight"
          />
          <EditableText
            value={heroContent.subtitle || 'See how our products are being used in real-world installations across Australia.'}
            onChange={(val) => updateBlockContent('hero', { subtitle: val })}
            tag="p"
            className="text-muted-foreground mt-2 max-w-2xl mx-auto"
          />
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-card border border-border rounded-sm animate-pulse">
                <div className="aspect-video bg-zinc-800" />
                <div className="p-6 space-y-3">
                  <div className="h-5 bg-zinc-800 rounded w-2/3" />
                  <div className="h-4 bg-zinc-800 rounded w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-16">
            <Briefcase className="h-16 w-16 text-zinc-600 mx-auto mb-4" />
            <p className="text-muted-foreground text-lg">No projects yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.map(project => (
              <Link key={project.id} to={`/projects/${project.slug}`} data-testid={`project-${project.slug}`}>
                <div className="bg-card border border-border rounded-sm overflow-hidden card-hover group h-full flex flex-col">
                  {project.gallery?.[0] && (
                    <div className="aspect-video relative overflow-hidden">
                      <img
                        src={project.gallery[0]}
                        alt={project.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex flex-wrap gap-2 mb-3">
                      {project.tags?.slice(0, 3).map(tag => (
                        <span key={tag} className="text-xs px-2 py-1 bg-[#78BE20]/10 text-[#78BE20] rounded-sm">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <h3 className="text-xl font-bold text-foreground group-hover:text-[#78BE20] transition-colors mb-2">
                      {project.title}
                    </h3>
                    <p className="text-muted-foreground text-sm line-clamp-3 flex-1">
                      {project.description}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
      <CMSBlockRenderer excludeTypes={['hero']} />
      <AdminToolbar />
    </Layout>
  );
}

export default function ProjectsPage() {
  return (
    <EditModeProvider pageSlug="projects">
      <ProjectsPageContent />
    </EditModeProvider>
  );
}
